/**
 * Admin 역할 CRUD. 라우트 admin.php roles 그룹과 동기화.
 */
import { Logger } from "../../utils/Logger";
import { AdminIndexTable } from "../components/AdminIndexTable";
import { Toast } from "../../utils/Toast";

function getCsrfToken(): string {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    if (meta?.content) return meta.content;
    const input = document.querySelector<HTMLInputElement>('input[name="_token"]');
    return input?.value ?? "";
}

function noop(controller: string, action: string): void {
    Logger.debug(`${controller}.${action} (no-op)`);
}

/** 권한 배치 저장 (체크박스 변경 → 저장 버튼으로 일괄 커밋) */
function initPermissionEdit(): void {
    const matrix  = document.getElementById("rp-matrix");
    const saveBtn = document.getElementById("rp-save-btn") as HTMLButtonElement | null;
    if (!matrix) return;

    const toggleUrl = matrix.dataset.toggleUrl ?? "";

    /** permKey → 저장될 상태 */
    const pending = new Map<string, boolean>();

    function updateSaveBadge(): void {
        if (!saveBtn) return;
        const count = pending.size;
        const badge = document.getElementById("rp-save-badge");
        if (badge) {
            badge.textContent = count > 0 ? String(count) : "";
            badge.hidden = count === 0;
        }
        saveBtn.disabled = count === 0;
    }

    // 체크박스 클릭 → UI 즉시 반영, pending 추적
    // label이 input을 감싸므로 브라우저가 합성 click을 input에도 발생시킴 → 중복 처리 방지
    matrix.addEventListener("click", (e: MouseEvent) => {
        if ((e.target as HTMLElement).tagName === "INPUT") return;
        const label = (e.target as HTMLElement).closest<HTMLElement>(".rp-checkbox");
        if (!label) return;

        const permKey   = label.dataset.permKey ?? "";
        if (!permKey) return;

        const isChecked = label.classList.contains("is-checked");
        const nextState = !isChecked;
        const origState = label.dataset.originalState === "1";

        label.classList.toggle("is-checked", nextState);
        const input = label.querySelector<HTMLInputElement>(".rp-checkbox__input");
        if (input) input.checked = nextState;

        if (nextState === origState) {
            pending.delete(permKey);
        } else {
            pending.set(permKey, nextState);
        }

        updateSaveBadge();
    });

    // 저장 버튼 클릭 → 순차 API 호출
    saveBtn?.addEventListener("click", async () => {
        if (pending.size === 0 || !saveBtn) return;

        const entries = Array.from(pending.entries());
        saveBtn.disabled = true;

        let failed = 0;

        for (const [permKey, enabled] of entries) {
            const body = new URLSearchParams();
            body.append("permission", permKey);
            body.append("enabled", enabled ? "1" : "0");
            body.append("_token", getCsrfToken());

            try {
                const res  = await fetch(toggleUrl, {
                    method: "POST",
                    headers: { "X-Requested-With": "XMLHttpRequest" },
                    body,
                });
                const data = await res.json() as { result?: string; msg?: string };

                if (data.result === "success") {
                    const lbl = matrix.querySelector<HTMLElement>(`[data-perm-key="${permKey}"]`);
                    if (lbl) lbl.dataset.originalState = enabled ? "1" : "0";
                    pending.delete(permKey);
                } else {
                    failed++;
                }
            } catch {
                failed++;
            }
        }

        updateSaveBadge();

        if (failed > 0) {
            Toast.error(`${failed}개 권한 저장에 실패했습니다.`);
        } else {
            Toast.success("권한 설정이 저장되었습니다.");
        }
    });

    updateSaveBadge();
}

export const RoleController = {
    index(): void {
        Logger.debug("RoleController.index");
        new AdminIndexTable().init();
    },
    create(): void {
        const preset = document.getElementById("preset") as HTMLSelectElement | null;
        const code   = document.getElementById("code")   as HTMLInputElement | null;
        const name   = document.getElementById("name")   as HTMLInputElement | null;
        const level  = document.getElementById("level")  as HTMLInputElement | null;
        if (!preset || !code || !name || !level) return;
        preset.addEventListener("change", () => {
            const opt = preset.options[preset.selectedIndex];
            if (opt.value === "") return;
            code.value  = opt.value;
            name.value  = opt.getAttribute("data-name")  ?? "";
            level.value = opt.getAttribute("data-level") ?? "0";
        });
    },
    store(): void {
        noop("RoleController", "store");
    },
    edit(): void {
        Logger.debug("RoleController.edit");
        initPermissionEdit();
    },
    update(): void {
        noop("RoleController", "update");
    },
    destroy(): void {
        noop("RoleController", "destroy");
    },
    permissions(): void {
        Logger.debug("RoleController.permissions");
        initPermissionEdit();
    },
};
